import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Alert,
  TextField, MenuItem, Box, CircularProgress, Typography, Paper,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import { fieldSx as compactFieldSx } from "../../egresos/components/formStyles";

// Multilínea compacto (igual que la observación del formulario de Egresos).

const isoFecha = (value) => (value ? String(value).slice(0, 10) : "");

const initialForm = {
  fecha: "", item: "", detalle: "", num_rec: "", monto: "",
  fecha_factura: "", nro_factura: "", nit: "", denominacion: "", nro_autorizacion: "", cod_control: "",
};

// El legacy (editar.php de Viático) no tenía campos de N° de Autorización ni Código de
// Control en el formulario de edición, aunque Guarda_editar.php sí los guarda — al no
// enviarse, cada edición de factura los borraba en silencio. Se agregan acá para
// corregir ese bug: precargados desde la factura existente, no se pierden al editar.
const EditarEgresoDialog = ({ open, onClose, egresoId, onEditado }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !egresoId) return;
    setLoading(true);
    setError("");
    Promise.all([
      axios.get(`/api/caja-viatico/egresos/${egresoId}`),
      axios.get("/api/caja-viatico/items"),
    ]).then(([resEgreso, resItems]) => {
      const egreso = resEgreso.data?.data;
      setItems(resItems.data?.data || []);
      setForm({
        fecha: isoFecha(egreso.fecha),
        item: egreso.item,
        detalle: egreso.descripcion || "",
        num_rec: egreso.num_recibo ? String(egreso.num_recibo) : "", // num_recibo es numérico en algunas cajas: sin String() fallaba el .trim() al guardar
        monto: String(egreso.monto),
        fecha_factura: isoFecha(egreso.factura?.fecha_factura),
        nro_factura: String(egreso.factura?.nro_factura || egreso.num_fac || ""),
        nit: egreso.factura?.nit || "",
        denominacion: egreso.factura?.denominacion || "",
        nro_autorizacion: egreso.factura?.nro_autorizacion || "",
        cod_control: egreso.factura?.cod_control || "",
      });
    }).catch((e) => setError(e?.response?.data?.message || "Error al cargar el egreso."))
      .finally(() => setLoading(false));
  }, [open, egresoId]);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const handleGuardar = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.patch(`/api/caja-viatico/egresos/${egresoId}`, {
        fecha: form.fecha,
        item: form.item,
        detalle: form.detalle.trim(),
        num_rec: form.num_rec.trim(),
        monto: Number(form.monto),
        factura: form.nro_factura.trim() ? {
          fecha_factura: form.fecha_factura,
          nro_factura: form.nro_factura.trim(),
          nit: form.nit.trim(),
          denominacion: form.denominacion.trim(),
          nro_autorizacion: form.nro_autorizacion.trim(),
          cod_control: form.cod_control.trim(),
          credito_fiscal: Number(form.monto),
        } : null,
      });
      onEditado();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };

  const puedeGuardar = form.fecha && form.item && Number(form.monto) > 0 && !saving;
  const tieneFactura = Boolean(form.nro_factura.trim());

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Editar Caja Viático #{egresoId}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
        ) : (
          <Stack spacing={0.6}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.6 }}>
              <TextField
                sx={compactFieldSx}
                size="small" type="date" label="Fecha de Registro" required
                InputLabelProps={{ shrink: true }}
                value={form.fecha} onChange={handleChange("fecha")}
              />
              <TextField
                sx={compactFieldSx}
                select size="small" label="Tipo Item" required
                value={form.item} onChange={handleChange("item")}
              >
                {items.map((it) => <MenuItem key={it.id} value={it.id}>{it.descripcion}</MenuItem>)}
              </TextField>
            </Box>
            <TextField
              sx={compactFieldSx}
              size="small" label="Detalle" name="detalle" autoComplete="on"
              value={form.detalle} onChange={handleChange("detalle")}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.6 }}>
              <TextField
                sx={compactFieldSx}
                size="small" label="N° Recibo"
                value={form.num_rec} onChange={handleChange("num_rec")}
              />
              <TextField
                sx={compactFieldSx}
                size="small" type="number" label="Monto" required
                value={form.monto} onChange={handleChange("monto")}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Box>

            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "action.hover" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                {tieneFactura ? "Editar Datos de Facturación" : "Registrar Datos de Facturación"}
              </Typography>
              <Stack spacing={0.6}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.6 }}>
                  <TextField
                    sx={compactFieldSx}
                    size="small" type="date" label="Fecha de Factura"
                    InputLabelProps={{ shrink: true }}
                    value={form.fecha_factura} onChange={handleChange("fecha_factura")}
                  />
                  <TextField
                    sx={compactFieldSx}
                    size="small" label="N° de Factura"
                    value={form.nro_factura} onChange={handleChange("nro_factura")}
                  />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 0.6 }}>
                  <TextField
                    sx={compactFieldSx}
                    size="small" label="NIT del proveedor"
                    value={form.nit} onChange={handleChange("nit")}
                  />
                  <TextField
                    sx={compactFieldSx}
                    size="small" label="Nombre o denominación"
                    value={form.denominacion} onChange={handleChange("denominacion")}
                  />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.6 }}>
                  <TextField
                    sx={compactFieldSx}
                    size="small" label="N° de Autorización"
                    value={form.nro_autorizacion} onChange={handleChange("nro_autorizacion")}
                  />
                  <TextField
                    sx={compactFieldSx}
                    size="small" label="Código de Control"
                    value={form.cod_control} onChange={handleChange("cod_control")}
                  />
                </Box>
              </Stack>
            </Paper>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleGuardar} disabled={!puedeGuardar || loading}>
          {saving ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditarEgresoDialog;
